import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import BeatLoader from "react-spinners/BeatLoader";
import TablePagination from "@mui/material/TablePagination";
import { toast } from "react-toastify";
import Modal from "../../components/Modal";
import TX_TYPES from "../../constants/transaction-types";
import TX_STATUSES from "../../constants/transaction-statuses";
import { MyContext } from "../../contexts/ContextProvider";
import {
  portalExportTransactionsCsv,
  portalGetTransaction,
  portalListTransactions,
} from "../../services/merchant-portal";
import "./portal.scss";

const emptyFilters = {
  status: "",
  type: "",
  currency: "",
  start: "",
  end: "",
};

const MerchantPortalTransactions = () => {
  const { currencyCode } = useContext(MyContext);
  const currencyOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    (currencyCode || []).forEach((item) => {
      const value = (item?.code || item?.name || "").trim();
      if (!value || seen.has(value)) return;
      seen.add(value);
      const label =
        item?.code && item?.name && item.code !== item.name
          ? `${item.code} — ${item.name}`
          : value;
      options.push({ value, label });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [currencyCode]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filters, setFilters] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const modalRef = React.useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await portalListTransactions({
        page: page + 1,
        size: rowsPerPage,
        ...applied,
      });
      if (res.status_code === 200) {
        setRows(res.data?.items || []);
        setTotal(res.data?.total ?? 0);
      } else {
        toast.error(res.message || "Could not load transactions");
      }
    } catch (err) {
      toast.error(`${err}`);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, applied]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = (e) => {
    e.preventDefault();
    const hasStart = Boolean(filters.start);
    const hasEnd = Boolean(filters.end);
    if (hasStart !== hasEnd) {
      toast.error("Select both start and end date, or clear both.");
      return;
    }
    if (hasStart && hasEnd && filters.start > filters.end) {
      toast.error("Start date must be on or before end date.");
      return;
    }
    setPage(0);
    setApplied({ ...filters });
  };

  const openDetail = (id) => {
    setDetailLoading(true);
    setDetail(null);
    modalRef.current?.open();
    portalGetTransaction(id)
      .then((res) => {
        if (res.status_code === 200) {
          setDetail(res.data);
        } else {
          toast.error(res.message || "Transaction not found");
        }
      })
      .catch((err) => toast.error(`${err}`))
      .finally(() => setDetailLoading(false));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await portalExportTransactionsCsv(applied);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `merchant-transactions-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export started");
    } catch (err) {
      toast.error(`${err}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-semibold">Transactions</h1>
        <button
          type="button"
          className="portal-btn portal-btn--secondary"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      <form
        onSubmit={applyFilters}
        className="portal-card portal-form mb-4 grid md:grid-cols-3 gap-3"
      >
        <div className="field mb-0">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={filters.status}
            onChange={(e) =>
              setFilters((f) => ({ ...f, status: e.target.value }))
            }
          >
            <option value="">All</option>
            {TX_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
        <div className="field mb-0">
          <label htmlFor="type">Type</label>
          <select
            id="type"
            value={filters.type}
            onChange={(e) =>
              setFilters((f) => ({ ...f, type: e.target.value }))
            }
          >
            <option value="">All</option>
            {TX_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <div className="field mb-0">
          <label htmlFor="currency">Currency</label>
          <select
            id="currency"
            value={filters.currency}
            onChange={(e) =>
              setFilters((f) => ({ ...f, currency: e.target.value }))
            }
          >
            <option value="">All</option>
            {currencyOptions.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field mb-0">
          <label htmlFor="start">Start date</label>
          <input
            id="start"
            type="date"
            value={filters.start}
            onChange={(e) =>
              setFilters((f) => ({ ...f, start: e.target.value }))
            }
          />
        </div>
        <div className="field mb-0">
          <label htmlFor="end">End date</label>
          <input
            id="end"
            type="date"
            value={filters.end}
            onChange={(e) =>
              setFilters((f) => ({ ...f, end: e.target.value }))
            }
          />
        </div>
        <div className="field mb-0 portal-filter-submit">
          <label className="portal-filter-submit-label" aria-hidden="true">
            &nbsp;
          </label>
          <button type="submit" className="portal-btn portal-filter-submit-btn">
            Apply filters
          </button>
        </div>
      </form>

      <div className="portal-card overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <BeatLoader color="#03C9D7" />
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left opacity-75 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 pr-3">Reference</th>
                  <th className="py-2 pr-3">Client</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Amount</th>
                  <th className="py-2 pr-3">Fee</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Sub account</th>
                  <th className="py-2 pr-3">Created</th>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((tx) => (
                  <tr
                    key={tx.external_reference_id}
                    className="border-b border-gray-100 dark:border-gray-800"
                  >
                    <td className="py-2 pr-3 font-mono text-xs">
                      {tx.external_reference_id}
                    </td>
                    <td className="py-2 pr-3">{tx.client}</td>
                    <td className="py-2 pr-3">{tx.transaction_type}</td>
                    <td className="py-2 pr-3">
                      {tx.amount} {tx.currency}
                    </td>
                    <td className="py-2 pr-3">{tx.fee ?? "—"}</td>
                    <td className="py-2 pr-3">{tx.status}</td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {tx.sub_account_id || "—"}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {tx.created_at}
                    </td>
                    <td className="py-2 pr-3">
                      <button
                        type="button"
                        className="text-[#03C9D7] text-sm"
                        onClick={() => openDetail(tx.external_reference_id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length ? (
              <p className="portal-muted py-6 text-center">No transactions found.</p>
            ) : null}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </>
        )}
      </div>

      <Modal
        ref={modalRef}
        title="Transaction detail"
        submitBtn="Close"
        onSubmit={() => modalRef.current?.close()}
        onClose={() => {}}
      >
        {detailLoading ? (
          <div className="flex justify-center py-8">
            <BeatLoader color="#03C9D7" />
          </div>
        ) : detail ? (
          <dl className="text-sm space-y-2">
            {[
              ["Reference", detail.external_reference_id],
              ["Client", detail.client],
              ["Type", detail.transaction_type],
              ["Amount", `${detail.amount} ${detail.currency}`],
              ["Fee", detail.fee],
              ["Status", detail.status],
              ["Sub account", detail.sub_account_id],
              ["Parent collection", detail.parent_reference],
              ["Message", detail.message],
              ["Created", detail.created_at],
              ["Updated", detail.updated_at],
            ].map(([label, value]) =>
              value != null && value !== "" ? (
                <div key={label} className="grid grid-cols-3 gap-2">
                  <dt className="opacity-75">{label}</dt>
                  <dd className="col-span-2 break-all">{value}</dd>
                </div>
              ) : null,
            )}
          </dl>
        ) : (
          <p className="portal-muted">No details.</p>
        )}
      </Modal>
    </div>
  );
};

export default MerchantPortalTransactions;
